import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';
import { IProductDetailPanelContentProps } from './content';
import { RatingBar } from '../../../components/rating-bar';
import { PriceDisplay } from '../price';
import { BwjPalette } from '../../../../app-core/app-theme';
import { InfoLine } from '../info-line';
import { PRODUCT_DETAIL_VARIANT_SELECTOR_ID } from '.';
import { ProductVariantSelector } from '../pdct-variant-select';
import { ProductVariantLabel } from '../pdct-variant-label';
import { BwjButton, BwjButtonType } from '../../../../framework/components/bwj-button';
import { isMeaningfulString } from '../../../../framework/utils/string';
import { RatingBreakdown } from '../rating-breakdown';
import { ProductImageBrowser } from '../pdct-image-browser';
import { PriceChangeNote } from './price-note';

const getStyleClasses = (theme: ITheme) => {
  const normalSpace = '0.75rem';
  const bigSpace = '1rem';
  const tinySpace = '0.1rem';
  const megaSpace = '2.5rem';

  const root: IStyle = {
    margin: 0,
    padding: 0,
  };

  const topFold: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: megaSpace,
  };

  const mediaContainer: IStyle = {}

  const detailsContainer: IStyle = {
    width: 1,
    flexGrow: 1,
    paddingLeft: bigSpace,
  }

  const brand: IStyle = {
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    fontWeight: 400,
    margin: 0,
    padding: 0,
  };

  const title: IStyle = {
    fontSize: theme.fonts.large.fontSize,
    lineHeight: theme.fonts.large.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginBottom: normalSpace,
  };

  const keyStatsContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: normalSpace,
    marginBottom: normalSpace,
  };

  const priceContainer: IStyle = {};

  const variantSelector: IStyle = {
    marginBottom: bigSpace,
  };

  const subheading: IStyle = {
    fontSize: theme.fonts.mediumPlus.fontSize,
    lineHeight: theme.fonts.mediumPlus.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginTop: bigSpace,
    marginBottom: normalSpace,
  };

  const actionContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: '2.5rem',
    gap: bigSpace,
  }

  const buttonContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: tinySpace,
  };

  const section: IStyle = {
    margin: 0,
    padding: 0,

    '& > ul': {
      margin: 0,
    },

    '& p': {
      margin: 0,
      padding: 0,
      flexGrow: 1,
      maxWidth: '55%',
    },
  }

  const reviewContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: megaSpace,

    '& > div': {
      flexGrow: 1,
    },
  };

  return mergeStyleSets({
    root,
    topFold,
    mediaContainer,
    detailsContainer,
    brand,
    title,
    keyStatsContainer,
    priceContainer,
    variantSelector,
    subheading,
    actionContainer,
    buttonContainer,
    section,
    reviewContainer,
  });
}

export const ProductDetailPanelContentDesktop: React.FunctionComponent<IProductDetailPanelContentProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { contentWidth } = props;
  const mediaWidth = useMemo(() => {
    let mw = Math.ceil(contentWidth / 2);
    if(mw < 320) {
      mw = 320;
    } else if ((contentWidth - mw) > 500) {
      mw = contentWidth - 500;
    }
    return mw;
  }, [contentWidth]);

  return (
    <div className={cs.root}>
      <div className={cs.topFold}>
        <div className={cs.mediaContainer}>
          <ProductImageBrowser
            id={props.id}
            idPrefix='prd-dtl'
            images={props.imageUrls}
            imageWidth={mediaWidth}
            recommendation={props.recommendation}
          />
        </div>
        <div className={cs.detailsContainer}>
          <h3 className={cs.brand}>{props.brand}</h3>
          <h2 className={cs.title}>{props.title}</h2>

          {((!!props.rating && !!props.averageRating) || !!props.price) &&
          <div className={cs.keyStatsContainer}>
            {!!props.rating && !!props.averageRating &&
            <RatingBar
              rating={props.averageRating}
              reviewerCount={props.rating.totalReviewCount}
              rootStyle={{ width: '75%', maxWidth: 250 }}
            />}
            {!!props.price &&
            <div className={cs.priceContainer}>
              <PriceDisplay
                price={props.price}
                fontSize={theme.fonts.superLarge}
              />
              {!!props.onIncludedServicesOverlayRequested && !!props.includedServices && props.includedServices.length > 0 &&
              <InfoLine
                icon={'LightBulb'}
                text={`Price includes ${props.includedServices.length} additional ${(props.includedServices.length > 1 ? 'services' : 'service')}`}
                onClick={() => !!props.onIncludedServicesOverlayRequested && props.onIncludedServicesOverlayRequested(props.includedServices!)}
              />}
            </div>}
            {props.priceChange !== undefined && props.priceChange !== 0 &&
            <PriceChangeNote priceChange={props.priceChange} />}
          </div>}

          {props.variants.length > 1 &&
          <div id={PRODUCT_DETAIL_VARIANT_SELECTOR_ID} className={cs.variantSelector}>
            <h3 className={cs.variantSelector}>
              Available In (select one)
            </h3>
            <ProductVariantSelector
              swatchSize={5}
              options={props.variants}
              selected={props.selectedVariant}
              showLabels={true}
              onSelectionChanged={s => !!s && props.onVariantSelected(s)}
            />
          </div>}
          <div className={cs.actionContainer}>
            {isMeaningfulString(props.chooseProductButtonText) &&
            <div className={cs.buttonContainer}>
              {!props.selectedVariant &&
              <InfoLine
                icon={'Warning'}
                text={`Please select a  ${props.variantDescriptor}`}
                color={BwjPalette.themeWarning}
                rootStyle={{ marginBottom: '0.1rem', justifyContent: 'center', }}
              />}
              <BwjButton
                buttonStyle={BwjButtonType.PrimaryStandard}
                text={props.chooseProductButtonText}
                disabled={!props.selectedVariant}
                onClick={() => props.onProductChosen(props.selectedVariant!)}
              />
            </div>}
            {!!props.selectedVariant &&
            <ProductVariantLabel
              texture={props.selectedVariant}
              size={1.4}
            />}
          </div>
        </div>
      </div>
      
      <section className={cs.section}>
        <h3 className={cs.subheading}>Key Features</h3>
        <ul>
          {props.keyFeatures.map(feature =>
          <li key={feature}>{feature}</li>)}
        </ul>
      </section>
      {!!props.includedServices && props.includedServices.length > 1 &&
      <section className={cs.section}>
        <h3 className={cs.subheading}>Included At No Additional Cost</h3>
        <ul>
          {props.includedServices.map(incl =>
          <li key={incl}>{incl}</li>)}
        </ul>
      </section>}
      {!!props.rating && !!props.averageRating &&
      <section className={cs.section}>
        <h3 className={cs.subheading}>{`Average Customer Rating: ${props.averageRating.toFixed(1)}`}</h3>
        <div className={cs.reviewContainer}>
          {isMeaningfulString(props.rating.reviewSummary) &&
          <p>{props.rating.reviewSummary}</p>}
          <RatingBreakdown review={props.rating} />
        </div>
      </section>}
    </div>
  );
};

